// Sugalarm — full-screen takeover for urgent_low / urgent_high
// alarms. Driven by `useAlarmEpisode`.
//
// Cycle 62: two-button model — Snooze + Stop (replaces cycle-20.x
// Address / Silence). Snooze starts the existing 5-minute countdown
// and re-arms; Stop silences the *current* episode's audio without
// terminating it (the underlying state machine keeps running off the
// reading stream and re-arms on a worse / fresh crossing). The
// cycle-38 terminal `silenced` state is no longer wired to a button
// here; the wire surface (/api/alerts/{id}/silence + the
// `alert_silenced` WS frame) stays as dormant plumbing.
//
// Cycle 73: per-zone alarm controls — **urgent_low loses Stop.**
// Hypoglycemic events must be actively addressed; a single tap must
// not be able to silence a low-side urgent for the rest of the
// episode. urgent_low active state renders Snooze only (centered);
// urgent_high keeps Snooze + Stop. The cycle-62 stopped-state UI
// (URGENT pill swapping to "Urgent Low · Stopped", muted dim,
// "Stopped — will re-arm if it gets worse." subtext) is unreachable
// for urgent_low after this cycle and is kept only for urgent_high.
// `useAlarmEpisode.stop()` has a defense-in-depth no-op for
// urgent_low so a stray caller can't bypass the UI gating. The
// cycle-66 cross-device shared-state model, the silenced_at
// lifecycle, the audio-bridge formula, and the engine re-arm path
// are all unchanged on the wire — only the UI driver is removed
// from urgent_low.
//
// Visual states (background pulses red while audio is on):
//
//   active && !hasSnoozed && !stopped
//     → primary CTA: "Snooze" + "Stop", red full takeover, audio on
//   snoozed
//     → "Snoozing — back in M:SS" countdown + Stop still available
//   stopped
//     → muted treatment (no flash, hero still shown), single line
//       "Stopped — will re-alarm if it gets worse" + a Snooze button
//
// `synthetic: true` skips the alerts API call inside `address()` /
// `stop()` so TestAlarmDriver can render a copy of this overlay
// against fake alert ids.

import { useCallback, useEffect, useState, useRef } from "react";

import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import { ascentHoldLine } from "../../lib/ascentLatch";
import type { TTSState } from "../../hooks/useTTS";
import {
  TREATING_IDENTITY_AUTO_DISMISS_MS,
  treatingErrorMessage,
} from "../../lib/treating";
import { TreatingSheet } from "./TreatingSheet";

interface Props {
  episode: AlarmEpisodeState;
  /** Retained on the prop surface so the overlay still receives the
   *  TTS handle for any future use; the urgent path no longer speaks. */
  tts: TTSState;
  /** Cycle 427: No Data mode — the twin of NowScreen's inline takeover
   *  (the two must stay in lockstep). The number greys to "No data" and
   *  its age rides the subtext; both buttons stay. Optional: the preview
   *  drives synthetic episodes and never sets it. */
  noData?: boolean;
  ageMinutes?: number | null;
}

function fmtSnooze(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

export function UrgentOverlay({
  episode,
  tts: _tts,
  noData = false,
  ageMinutes = null,
}: Props) {
  const {
    phase,
    level,
    sgv,
    hasSnoozed,
    snoozeRemainingMs,
    stopped,
    treating,
  } = episode;

  // Cycle 391: local, transient sheet state. Hooks run before the early
  // returns below so the hook order is stable across every render.
  const [treatingSheetOpen, setTreatingSheetOpen] = useState(false);
  const [treatingError, setTreatingError] = useState<string | null>(null);
  const [treatingBusy, setTreatingBusy] = useState(false);
  // Latches once the window is observed OPEN while the name step is up.
  const identityWindowSeenRef = useRef(false);
  // ⚠ CYCLE 404 — ONE TAP. The duration menu is gone: there is a single
  // ten-minute window and the SERVER assigns it, so this commits
  // immediately and then asks the optional name question.
  const beginTreating = useCallback(async () => {
    setTreatingBusy(true);
    const code = await episode.treat();
    setTreatingBusy(false);
    if (code) {
      setTreatingError(treatingErrorMessage(code));
      return;
    }
    setTreatingError(null);
    // The window is OPEN. Ask who, without blocking anything.
    setTreatingSheetOpen(true);
  }, [episode]);
  useEffect(() => {
    if (level !== "urgent_low" || phase === "idle") {
      setTreatingSheetOpen(false);
    }
  }, [level, phase]);

  // ⚠ THE NAME STEP MUST NEVER STAND BETWEEN A CAREGIVER AND A RE-ARMED
  // ALARM. It is explicitly optional, so people walk away from it, and
  // its scrim covers the alarm surface — if a breakthrough tears the
  // window down while it is up, Snooze would be visible but NOT TAPPABLE
  // behind an unattended prompt, which breaks the cycle-63/81 floor
  // (reachable, not merely on screen). Closes the instant the window ends.
  //
  // ⚠ AND IT MUST NOT CLOSE BEFORE THE WINDOW HAS BEEN *SEEN* OPEN.
  // On a 2nd/3rd tap `treatingUntilMs` prefers server truth once
  // `treatingCount > 0`, so `treating` is still FALSE for the round-trip
  // between the POST resolving and the `alert_treating` frame landing — a
  // naive `!treating` close fires in that gap and dismisses the name step
  // instantly. It fails safe (closed), but it silently deletes the feature
  // on exactly the repeat taps a household is most anxious about. So the
  // close arms only after we have observed the window actually open for
  // THIS step; the self-dismiss belt below still covers a window that
  // never opens at all.
  useEffect(() => {
    if (!treatingSheetOpen) {
      identityWindowSeenRef.current = false;
      return;
    }
    if (treating) {
      identityWindowSeenRef.current = true;
      return;
    }
    if (identityWindowSeenRef.current) {
      setTreatingSheetOpen(false);
    }
  }, [treatingSheetOpen, treating]);

  // Belt for the case where it simply goes unanswered.
  useEffect(() => {
    if (!treatingSheetOpen) return;
    const id = window.setTimeout(
      () => setTreatingSheetOpen(false),
      TREATING_IDENTITY_AUTO_DISMISS_MS,
    );
    return () => window.clearTimeout(id);
  }, [treatingSheetOpen]);

  if (phase === "idle" || level == null || sgv == null) return null;

  // Cycle 398: the in-range stand-down, for symmetry with every other alarm
  // display surface.
  //
  // ⚠ THIS COMPONENT IS PREVIEW-ONLY TODAY. The LIVE urgent full-screen
  // takeover is NowScreen's inline `isUrgentEpisode` view (the two have been
  // documented as a lockstep pair since cycle 73); <PreviewOverlay> is the
  // only remaining importer of this file. So this term is INERT here by two
  // independent constructions — the preview pins `bannerSuppressedInRange:
  // false`, and `inRangeStandDown` returns false for any `synthetic`
  // episode. It is written anyway so that if this surface is ever remounted
  // for a real episode it does not silently become the one place that keeps
  // painting a stale urgent takeover.
  if (episode.bannerSuppressedInRange) return null;

  // Cycle 33: urgent-only. Regular `low` episodes get the thin
  // LowAlarmBanner at the top of the Now screen instead of the
  // full-page red takeover.
  if (level !== "urgent_low" && level !== "urgent_high") return null;

  // Cycle 42 + cycle 61: an urgent episode collapses to the thin
  // <UrgentBanner> the moment the parent presses Snooze
  // (`hasSnoozed` flips true) or while the snooze countdown runs —
  // the Now screen comes back so the correction can be watched.
  // Cycle 42 carved `urgent_high` out of the full takeover; cycle 61
  // extended the carve-out to `urgent_low`.
  // Cycle 187: **Stop** now collapses too. Through cycle 62/67 Stop
  // held the full-bleed overlay in a muted treatment; this cycle
  // supersedes that for the urgent family so a Stopped urgent yields
  // to the thin <UrgentBanner> (which now carries the muted "Stopped"
  // state). This overlay is mounted ONLY by <PreviewOverlay> (cycle
  // 81), so widening the gate keeps the Test-alarms preview faithful
  // to the live Now screen — a stopped synthetic urgent collapses to
  // the same banner the preview mounts beside this overlay. Only an
  // ACTIVE urgent keeps the full takeover here.
  // Cycle 391: an open treating window collapses this takeover too — the
  // same rule the inline NowScreen view applies, so the two surfaces stay
  // in lockstep (the standing requirement for these twin takeovers).
  if (
    (level === "urgent_high" || level === "urgent_low") &&
    (hasSnoozed || phase === "snoozed" || stopped || treating)
  ) {
    return null;
  }

  const isUrgent = level === "urgent_low" || level === "urgent_high";
  const isLow = level === "urgent_low";

  const tone = level === "urgent_low" ? "Urgent low" : "Urgent high";

  // Cycle 424: the twin of NowScreen's inline takeover — the two must stay
  // in lockstep. While the member climbs out of an urgent low the subtext
  // says the low rung is held (copy only, same slot).
  const subtext = stopped
    ? "Stopped — will re-alarm if it gets worse."
    : noData
      ? `No reading${ageMinutes != null ? ` for ${Math.max(0, Math.floor(ageMinutes))} min` : ""}. Calls continue.`
      : episode.ascentLatched
        ? ascentHoldLine(null)
        : isLow
          ? "Treat now. Eat or drink fast-acting carbs."
          : "Check ketones. Hydrate. Consider correction.";

  // Cycle 62: button + label depend on phase + history. Snooze + Stop
  // are the only two buttons; Stop is always visible alongside Snooze
  // while the alarm is sounding. In the muted (stopped) state only
  // Snooze is offered — Stop is already "pressed" by definition.
  // `showSnoozeText` is the countdown treatment shown after Snooze
  // has fired (phase === "snoozed"), but cycle 61 collapses snoozed
  // urgent to the banner so this branch is effectively unreachable
  // in current routing; the dead branch is kept for safety if a
  // future cycle re-enables the snoozed-overlay path.
  const showActiveButtons =
    phase === "active" && !hasSnoozed && !stopped;
  const showStoppedButtons = stopped;
  const showSnoozeText = phase === "snoozed";

  return (
    <div
      role="alertdialog"
      aria-labelledby="sg-alarm-title"
      style={{
        position: "absolute",
        inset: 0,
        backgroundColor: "var(--zone-urgent)",
        display: "flex",
        flexDirection: "column",
        // Cycle 392: compact to the TOP while a sheet is open so the
        // bottom-anchored sheet cannot cover the live value.
        justifyContent: treatingSheetOpen ? "flex-start" : "center",
        alignItems: "center",
        // Cycle 36.2: bump the top/bottom padding by the iOS
        // safe-area insets. The overlay is full-bleed red, but the
        // URGENT pill at the top would otherwise sit behind the
        // iPhone status bar and the Address button at the bottom
        // would conflict with the home indicator on tall screens.
        // env() returns 0 on desktop / Android where there's no
        // inset, so this is a no-op there.
        paddingTop: "calc(24px + env(safe-area-inset-top, 0px))",
        paddingRight: 24,
        paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
        paddingLeft: 24,
        zIndex: 100,
        // Flashing alarm background — full-bleed red that pulses
        // between two saturations. Big and unmistakable, but not
        // strobing. Cycle 62: when `stopped`, drop the flash (audio
        // is muted; the visual stays full-bleed red so the urgent
        // level is still impossible to miss, but the strobe is
        // taken off so it reads as muted-but-watching). The enter
        // animation still runs for the initial mount.
        animation: stopped
          ? "urgentEnter 180ms ease-out"
          : "urgentEnter 180ms ease-out, alarmFlash 1.1s ease-in-out infinite",
        color: "#fff",
        opacity: stopped ? 0.92 : 1,
      }}
    >
      <div style={{ position: "relative", textAlign: "center" }}>
        <div
          id="sg-alarm-title"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            background: "rgba(0, 0, 0, 0.35)",
            color: "#fff",
            padding: "8px 18px",
            borderRadius: 999,
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: "0.06em",
            marginBottom: 18,
            textTransform: "uppercase",
          }}
        >
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: "#fff",
              // Cycle 62: drop the dot pulse when stopped so the
              // muted state reads visually different from the active
              // alarm at-a-glance.
              animation: stopped
                ? undefined
                : "pulseDot 0.9s ease-in-out infinite",
              opacity: stopped ? 0.7 : 1,
            }}
          />
          {stopped ? `${tone} · Stopped` : tone}
        </div>
        <div
          className="tnum"
          style={{
            fontFamily: "var(--hero-font)",
            fontWeight: 700,
            // Cycle 36.1: clamp instead of a constant 320px. On a
            // 390 px mobile viewport a 3-digit value like 280 would
            // blow past the screen edge; the lower bound keeps the
            // number visible with room for the unit underneath,
            // while the upper bound caps growth at the kiosk +
            // desktop size the user was happy with.
            fontSize: treatingSheetOpen
              ? "clamp(56px, 10vw, 120px)"
              : noData
                ? "clamp(56px, 12vw, 160px)"
                : "clamp(96px, 24vw, 320px)",
            lineHeight: 0.82,
            letterSpacing: noData ? "-0.02em" : "-0.045em",
            color: noData ? "rgba(255, 255, 255, 0.62)" : "#fff",
            textShadow: "0 6px 24px rgba(0, 0, 0, 0.45)",
            whiteSpace: "nowrap",
          }}
        >
          {noData ? "No data" : sgv}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#fff",
            opacity: 0.92,
            marginTop: 10,
            marginBottom: 22,
            letterSpacing: "0.01em",
          }}
        >
          {subtext}
        </div>

        {showActiveButtons && (
          <div
            style={{
              display: "inline-flex",
              gap: 16,
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              onClick={() => {
                void episode.address();
              }}
              style={primaryBtnStyle}
            >
              Snooze
            </button>
            {/* Cycle 391: urgent low ONLY — Snooze gains a partner.
                "I'm treating this" is NOT Stop: it opens a bounded,
                self-expiring, breakable window and the cycle-73 floor
                (no Stop on urgent low, anywhere) is untouched. */}
            {isLow && (
              <button
                type="button"
                onClick={() => {
                  setTreatingError(null);
                  void beginTreating();
                }}
                disabled={treatingBusy}
                style={secondaryBtnStyle}
              >
                {treatingBusy ? "Starting\u2026" : "I'm treating this"}
              </button>
            )}
            {/* Cycle 73: Stop is hidden on urgent_low. The Snooze
                button above stays the cycle-62 size; we do not
                shrink-to-fit or stretch when it becomes the only
                control. */}
            {!isLow && (
              <button
                type="button"
                onClick={episode.stop}
                style={secondaryBtnStyle}
              >
                Stop
              </button>
            )}
          </div>
        )}

        {/* ⚠ CYCLE 404 — THE REFUSAL NEEDS A HOME NOW THAT THE SHEET DOES
            NOT OPEN FIRST. It used to render inside the duration step;
            with one tap there is no sheet to hold it, and the refusal that
            MUST reach the caregiver is the dark sensor: the alarm is still
            sounding and the reason is the feed, not the button. */}
        {/* Cycle 427: the cap line that stood here is gone with the cap
            (operator-authorized 2026-09-08): every window is ten minutes. */}
        {treatingError && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              color: "#fff",
              fontSize: 18,
              textAlign: "center",
              maxWidth: 640,
            }}
          >
            {treatingError}
          </div>
        )}

        {/* Cycle 73: the stopped (muted) state is now reachable only
            from urgent_high (urgent_low can no longer be stopped).
            Gating on `!isLow` makes that explicit and prevents a
            stray render path from ever painting "Urgent Low ·
            Stopped". */}
        {showStoppedButtons && !isLow && (
          <button
            type="button"
            onClick={() => {
              void episode.address();
            }}
            style={primaryBtnStyle}
            aria-label="Snooze (audio paused; re-arm on worse crossing)"
          >
            Snooze
          </button>
        )}

        {showSnoozeText && (
          <div
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              padding: "16px 28px",
              background: "rgba(0, 0, 0, 0.35)",
              borderRadius: 999,
              border: "1px solid rgba(255, 255, 255, 0.4)",
              color: "#fff",
            }}
          >
            <span
              style={{
                fontSize: "var(--type-floor)",
                fontWeight: 800,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255, 255, 255, 0.85)",
              }}
            >
              Snoozing
            </span>
            <span
              className="tnum"
              style={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}
            >
              {fmtSnooze(snoozeRemainingMs ?? 0)}
            </span>
            <span
              style={{
                fontSize: "var(--type-floor)",
                color: "rgba(255, 255, 255, 0.85)",
              }}
            >
              Alarm resumes if {isLow ? "still low" : "still high"}
            </span>
          </div>
        )}

        {/* Reference to `isUrgent` keeps tsc honest; future copy can
            differentiate urgent vs. plain levels without refactoring. */}
        {isUrgent ? null : null}
      </div>
      {/* Cycle 391: rendered INSIDE the overlay so the dimmed urgent
          context stays visible behind the sheet. */}
      {treatingSheetOpen && isLow && (
        <TreatingSheet
          onIdentity={(contactId: string | null) => {
            // Optimistic: close immediately. The confirmation arrives
            // over `alert_treating`, and a failed post must not reopen
            // the sheet or raise an error over a live urgent low.
            setTreatingSheetOpen(false);
            void episode.setTreatingIdentity(contactId);
          }}
          onDismissIdentity={() => setTreatingSheetOpen(false)}
        />
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  appearance: "none",
  border: 0,
  background: "#fff",
  color: "#8a1a1a",
  borderRadius: 999,
  padding: "20px 48px",
  fontWeight: 800,
  fontSize: 20,
  minHeight: 64,
  letterSpacing: "0.02em",
  cursor: "pointer",
  boxShadow: "0 6px 18px rgba(0, 0, 0, 0.35)",
};

// Cycle 62: Stop is the secondary action — distinct color/weight from
// Snooze so a panicked-glance tap is unambiguous. Outlined-white-on-
// red mirrors common destructive-confirm UI patterns; the larger
// 64-px height matches the Snooze button so the two read as a real
// button pair.
const secondaryBtnStyle: React.CSSProperties = {
  appearance: "none",
  border: "2px solid #fff",
  background: "transparent",
  color: "#fff",
  borderRadius: 999,
  padding: "18px 44px",
  fontWeight: 700,
  fontSize: 19,
  minHeight: 64,
  letterSpacing: "0.04em",
  cursor: "pointer",
  textTransform: "uppercase",
  fontFamily: "inherit",
};
